import './style.css';
import { createRouter, type Routes } from './router.js';
import {
  dashboardPage,
  pnlPage,
  expenseFormPage,
  loginPage,
  signupPage,
  onboardingPage,
  resetPasswordPage,
} from './pages.js';
import { profitTodayPage } from './pages-today.js';
import { simpleExpensePage } from './pages-simple-expense.js';
import { skuProfitPage } from './pages-sku-profit.js';
import { planPage } from './pages-plan.js';
import { entriesPage, payrollPage, assetsPage, taxPage } from './screens.js';
import { skuCostPage } from './screens-catalog.js';
import { inventoryPage } from './screens-inventory.js';
import { dataQualityPage } from './screens-quality.js';
import { importPage } from './screens-import.js';
import { ordersPage } from './screens-orders.js';
import { mappingPage } from './screens-mapping.js';
import { isConfigured } from './config.js';
import { getSession, signOut, consumeAuthHash, storeSessionFromToken } from './auth.js';
import {
  getContext,
  getPnlCompare,
  getPnlDaily,
  getPnlByChannel,
  tiktokComplete,
  type AccountContext,
} from './api.js';
import { setView, liveView, demoView } from './view.js';
import { setTenant, getTenant, getUiMode, setUiMode, type UiMode } from './state.js';
import {
  initFilters,
  getFilters,
  setFilters,
  onFiltersChanged,
  syncUrl,
  applyUrlFilters,
} from './filters.js';

const NAV = [
  {
    group: 'ภาพรวม',
    items: [
      ['กำไรวันนี้', 'today'],
      ['แดชบอร์ด', 'dashboard'],
      ['รายงานกำไรขาดทุน (P&L)', 'pnl'],
      ['ส่งออก', ''],
    ],
  },
  {
    group: 'ยอดขาย',
    items: [
      ['ออเดอร์และเงินโอน', 'orders'],
      ['นำเข้า/ซิงค์', 'import'],
    ],
  },
  {
    group: 'ต้นทุน',
    items: [
      ['ตัวทำเงิน / ตัวขาดทุน', 'skuprofit'],
      ['สินค้าและต้นทุน SKU', 'skucost'],
      ['ซื้อสินค้าและสต็อก', 'inventory'],
      ['การปันส่วน', 'mapping'],
    ],
  },
  {
    group: 'ค่าใช้จ่ายและบัญชี',
    items: [
      ['บันทึกรายรับ-รายจ่าย', 'add'],
      ['กรอกรายได้/ค่าใช้จ่าย', 'entry'],
      ['รายการรายได้/ค่าใช้จ่าย', 'entries'],
      ['เงินเดือน (Payroll)', 'payroll'],
      ['ทรัพย์สิน', 'assets'],
      ['ภาษีและปรับปรุง', 'tax'],
    ],
  },
  {
    group: 'ควบคุมระบบ',
    items: [
      ['แพ็กเกจและการใช้งาน', 'plan'],
      ['ตั้งค่าการจับคู่ (Mapping)', 'mapping'],
      ['คุณภาพข้อมูลและประวัติแก้ไข (Audit)', 'quality'],
    ],
  },
];

/** โหมดง่าย: 5 ปลายทางหลัก (แม่ค้าทั่วไป) */
const SIMPLE_PRIMARY: [string, string, string][] = [
  ['หน้าแรก', 'today', '🏠'],
  ['บันทึกรายรับ-รายจ่าย', 'add', '➕'],
  ['ออเดอร์ & ยอดขาย', 'orders', '📦'],
  ['สินค้า & ต้นทุน', 'skucost', '🏷️'],
  ['รายงานกำไร', 'pnl', '📊'],
];
/** โหมดง่าย: เมนูที่เหลือ พับไว้ใน "เพิ่มเติม" */
const SIMPLE_MORE: [string, string][] = [
  ['ตัวทำเงิน / ตัวขาดทุน', 'skuprofit'],
  ['รายการรายรับ-รายจ่าย', 'entries'],
  ['ซื้อสินค้าและสต็อก', 'inventory'],
  ['เงินเดือน', 'payroll'],
  ['ทรัพย์สิน', 'assets'],
  ['ภาษีและปรับปรุง', 'tax'],
  ['ตั้งค่าการจับคู่ / ปันส่วน', 'mapping'],
  ['นำเข้า/ซิงค์', 'import'],
  ['แดชบอร์ดแบบละเอียด', 'dashboard'],
  ['คุณภาพข้อมูล / ประวัติ', 'quality'],
  ['แพ็กเกจและการใช้งาน', 'plan'],
];
const SIMPLE_ROUTES = new Set<string>([
  ...SIMPLE_PRIMARY.map((x) => x[1]),
  ...SIMPLE_MORE.map((x) => x[1]),
]);

/** ปุ่มสลับโหมด ง่าย/บัญชีเต็ม */
function modeToggleHtml(): string {
  const m = getUiMode();
  return `<div class="nav-group mode-switch">
    <h4>โหมดใช้งาน</h4>
    <div class="mode-toggle">
      <button type="button" class="mode-btn${m === 'simple' ? ' on' : ''}" data-mode="simple">ง่าย</button>
      <button type="button" class="mode-btn${m === 'advanced' ? ' on' : ''}" data-mode="advanced">บัญชีเต็ม</button>
    </div>
  </div>`;
}

function navLink(label: string, route: string, active: string, icon = ''): string {
  const cls = route === active ? 'nav-item active' : 'nav-item';
  return `<a class="${cls}" href="#${route}">${icon ? icon + ' ' : ''}${label}</a>`;
}
/** โหมดง่าย: 5 หลัก + "เพิ่มเติม" (พับ; เปิดค้างถ้าหน้าปัจจุบันอยู่ในนั้น) */
function simpleMenuHtml(active: string): string {
  const primary = SIMPLE_PRIMARY.map(([l, r, ic]) => navLink(l, r, active, ic)).join('');
  const more = SIMPLE_MORE.map(([l, r]) => navLink(l, r, active)).join('');
  const open = SIMPLE_MORE.some(([, r]) => r === active) ? ' open' : '';
  return `<div class="nav-group nav-primary">${primary}</div>
    <details class="nav-more"${open}><summary>เพิ่มเติม</summary><div class="nav-more-items">${more}</div></details>`;
}
/** โหมดบัญชีเต็ม: กลุ่มครบเหมือนเดิม */
function advancedMenuHtml(active: string): string {
  return NAV.map((g) => {
    return `<div class="nav-group"><h4>${g.group}</h4>${g.items
      .map(([label, route]) => {
        const cls = route === active ? 'nav-item active' : route ? 'nav-item' : 'nav-item disabled';
        const href = route ? `#${route}` : '#dashboard';
        const dis = route ? '' : ' aria-disabled="true"';
        return `<a class="${cls}" href="${href}"${dis}>${label}</a>`;
      })
      .join('')}</div>`;
  }).join('');
}
function navHtml(active: string): string {
  const menu =
    getUiMode() === 'simple' ? simpleMenuHtml(active) : advancedMenuHtml(active);
  const session = getSession();
  const foot = session
    ? `<div class="nav-group"><a class="nav-item" id="logout" href="#">ออกจากระบบ (${session.email})</a></div>`
    : '';
  return businessSelectorHtml() + modeToggleHtml() + menu + foot;
}

const app = document.querySelector<HTMLDivElement>('#app')!;
app.innerHTML = `
  <a class="skip-link" id="skip-link" href="#">ข้ามไปยังเนื้อหาหลัก</a>
  <div class="layout">
    <aside class="sidebar"><div class="brand">RRP P&amp;L<small>ระบบแดชบอร์ดกำไรขาดทุน</small></div><nav id="nav"></nav></aside>
    <main class="main" id="outlet" tabindex="-1"></main>
  </div>`;
const outlet = app.querySelector<HTMLElement>('#outlet')!;

// ข้ามเมนูไปยังเนื้อหา — ใช้ focus แทนการเปลี่ยน hash (เพราะ hash คือ route ของแอป)
app.querySelector<HTMLAnchorElement>('#skip-link')?.addEventListener('click', (e) => {
  e.preventDefault();
  outlet.focus();
  outlet.scrollIntoView({ block: 'start' });
});
const nav = app.querySelector<HTMLElement>('#nav')!;

function wireNav(): void {
  nav.querySelector('#logout')?.addEventListener('click', (e) => {
    e.preventDefault();
    signOut();
    start();
  });
  nav.querySelector<HTMLSelectElement>('#biz-select')?.addEventListener('change', (e) => {
    selectBusiness((e.target as HTMLSelectElement).value);
  });
  nav.querySelectorAll<HTMLButtonElement>('.mode-btn[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      const mode = b.dataset['mode'] as UiMode;
      if (mode === getUiMode()) return;
      setUiMode(mode);
      const cur = location.hash.replace(/^#/, '').split('?')[0] || 'today';
      // โหมดง่ายแต่อยู่หน้าที่ถูกซ่อน → กลับหน้ากำไรวันนี้ (เปลี่ยน hash → router วาดใหม่)
      if (mode === 'simple' && !SIMPLE_ROUTES.has(cur)) {
        location.hash = 'today';
      } else {
        nav.innerHTML = navHtml(cur);
        wireNav();
      }
    }),
  );
}

function businessSelectorHtml(): string {
  const tenant = getTenant();
  if (!tenant || accountsCtx.length === 0) return '';
  const options = accountsCtx
    .flatMap((a) =>
      a.businesses.map(
        (b) =>
          `<option value="${b.id}"${b.id === tenant.businessId ? ' selected' : ''}>${a.accountName} · ${b.name}</option>`,
      ),
    )
    .join('');
  return `<div class="nav-group"><h4>กิจการ</h4><select id="biz-select" class="biz-select">${options}</select></div>`;
}

function mountAppRouter(): void {
  const routes: Routes = {
    today: profitTodayPage,
    add: simpleExpensePage,
    skuprofit: skuProfitPage,
    dashboard: dashboardPage,
    pnl: pnlPage,
    entry: expenseFormPage,
    entries: entriesPage,
    payroll: payrollPage,
    skucost: skuCostPage,
    assets: assetsPage,
    tax: taxPage,
    inventory: inventoryPage,
    quality: dataQualityPage,
    import: importPage,
    orders: ordersPage,
    mapping: mappingPage,
    plan: planPage,
  };

  /**
   * โหลดข้อมูลตามตัวกรองปัจจุบัน (live = API, demo = ชุดตัวอย่าง) แล้ววาดหน้าใหม่
   * ระหว่างโหลด ทำเครื่องหมาย busy เพื่อกันกดส่งออก/เจาะข้อมูลด้วยช่วงเก่า
   */
  const reloadAndRender = async (): Promise<void> => {
    outlet.dataset['loading'] = '1';
    outlet.setAttribute('aria-busy', 'true');
    if (getTenant()) {
      try {
        await loadReport();
      } catch {
        // แสดงข้อมูลเดิมไว้ก่อน ผู้ใช้กดใหม่ได้
      }
    } else {
      const f = getFilters();
      setView(demoView({ from: f.from, to: f.to }));
    }
    rerender?.();
    delete outlet.dataset['loading'];
    outlet.removeAttribute('aria-busy');
  };

  // ต้องผูกก่อนสร้าง router: เปิดลิงก์ที่มีตัวกรอง/กด back-forward → อ่าน URL ก่อน render
  window.addEventListener('hashchange', () => {
    if (!applyUrlFilters()) return;
    void reloadAndRender();
  });

  const router = createRouter(routes, outlet, (path) => {
    nav.innerHTML = navHtml(path);
    wireNav();
    syncUrl();
  });
  rerender = router.render;
  onFiltersChanged(reloadAndRender);
  // บันทึก/แก้/กลับรายการ → รีเฟรชหน้าปัจจุบันอัตโนมัติ (เฟส 3)
  window.addEventListener('rrp:data-changed', () => void reloadAndRender());
  router.render();
}

function renderAuthPage(page: { render(): string; mount?(el: HTMLElement): void }): void {
  nav.innerHTML = '';
  outlet.innerHTML = page.render();
  page.mount?.(outlet);
}
function showLogin(): void {
  renderAuthPage(loginPage(() => start(), showSignup));
}
function showSignup(): void {
  // สมัครสำเร็จ+ได้ session → start() (ไม่มีกิจการ → onboarding); กลับ = login
  renderAuthPage(signupPage(() => start(), showLogin));
}
function showOnboarding(): void {
  // สร้างกิจการแรกเสร็จ → start() ใหม่ (คราวนี้มีกิจการ → เข้าแอป)
  renderAuthPage(onboardingPage(() => start()));
}
function showResetPassword(auth: ReturnType<typeof consumeAuthHash>): void {
  // ตั้งรหัสใหม่เสร็จ → มี session แล้ว → start() เข้าแอป
  renderAuthPage(resetPasswordPage(auth!, () => start()));
}

let accountsCtx: AccountContext[] = [];
let rerender: (() => void) | null = null;

/** โหลดรายงานตามตัวกรองปัจจุบัน (ช่วงวันที่ + งวดเปรียบเทียบ + กิจการ) */
async function loadReport(): Promise<void> {
  const tenant = getTenant();
  if (!tenant) return;
  const f = getFilters();
  const [compare, daily, channels] = await Promise.all([
    getPnlCompare(tenant.accountId, tenant.businessId, f.from, f.to, f.compare),
    getPnlDaily(tenant.accountId, tenant.businessId, f.from, f.to),
    getPnlByChannel(tenant.accountId, tenant.businessId, f.from, f.to),
  ]);
  setView(
    liveView({
      current: compare.current,
      comparison: compare.comparison,
      range: { from: f.from, to: f.to },
      compareRange: compare.comparison.range,
      daily: daily.points,
      channelMix: channels.items,
    }),
  );
}

/** เลือกกิจการ/ร้านจาก context แล้วโหลดรายงานใหม่ */
function selectBusiness(businessId: string): void {
  for (const acc of accountsCtx) {
    const biz = acc.businesses.find((b) => b.id === businessId);
    if (!biz) continue;
    setTenant({
      accountId: acc.accountId,
      businessId: biz.id,
      accountName: acc.accountName,
      businessName: biz.name,
    });
    setFilters({ businessId: biz.id });
    return;
  }
}

type BootResult = 'ok' | 'no-account' | 'auth-error';
async function bootstrapLive(): Promise<BootResult> {
  let ctx: Awaited<ReturnType<typeof getContext>>;
  try {
    ctx = await getContext();
  } catch {
    return 'auth-error'; // token หมดอายุ/เรียก API ไม่ได้ → ให้ล็อกอินใหม่
  }
  accountsCtx = ctx.accounts;
  const acc = ctx.accounts[0];
  const biz = acc?.businesses[0];
  if (!acc || !biz) return 'no-account'; // session ใช้ได้ แต่ยังไม่มีกิจการ → onboarding

  // ตัวกรองจาก URL ก่อน (แชร์ลิงก์ได้) ไม่งั้นใช้กิจการแรก + เดือนปัจจุบัน
  const f = initFilters({ businessId: biz.id });
  const selected =
    ctx.accounts
      .flatMap((a) => a.businesses.map((b) => ({ acc: a, biz: b })))
      .find((x) => x.biz.id === f.businessId) ?? { acc, biz };
  setTenant({
    accountId: selected.acc.accountId,
    businessId: selected.biz.id,
    accountName: selected.acc.accountName,
    businessName: selected.biz.name,
  });
  syncUrl();
  try {
    await loadReport();
  } catch {
    /* รายงานพลาด (เช่นยังไม่มีข้อมูล) — เข้าแอปได้ หน้าจะแสดง error/empty เอง */
  }
  return 'ok';
}

/**
 * จบ TikTok OAuth: TikTok redirect กลับเว็บพร้อม ?code&state → เรียก callback (authed)
 * แล้วเก็บผลไว้ใน sessionStorage ให้หน้า Orders แสดง, ล้าง query, ไปหน้า #orders
 */
async function handleTikTokRedirect(): Promise<void> {
  let pending = false;
  try {
    pending = sessionStorage.getItem('tt_pending') === '1';
  } catch {
    /* ignore */
  }
  const p = new URLSearchParams(location.search);
  const code = p.get('code');
  const state = p.get('state');
  if (!pending || !code || !state) return;
  try {
    sessionStorage.removeItem('tt_pending');
  } catch {
    /* ignore */
  }
  try {
    const r = await tiktokComplete(code, state);
    const result = r.ok ? `connected:${r.shopsLinked ?? 0}` : `error:${r.reason ?? ''}`;
    try {
      sessionStorage.setItem('tt_result', result);
    } catch {
      /* ignore */
    }
  } catch (e) {
    try {
      sessionStorage.setItem('tt_result', `error:${(e as Error).message}`);
    } catch {
      /* ignore */
    }
  }
  // ล้าง code/state ออกจาก URL แล้วไปหน้าออเดอร์
  history.replaceState(null, '', `${location.pathname}#orders`);
}

async function start(): Promise<void> {
  // มาจากลิงก์อีเมล (recovery/ยืนยัน) — จัดการ token ใน URL hash ก่อน routing ปกติ
  const authHash = isConfigured ? consumeAuthHash() : null;
  if (authHash?.type === 'recovery') {
    showResetPassword(authHash); // ตั้งรหัสใหม่ก่อน
    return;
  }
  if (authHash) {
    storeSessionFromToken(authHash.accessToken, authHash.expiresAt); // ยืนยันอีเมล → auto-login
  }

  if (isConfigured && getSession()) {
    outlet.innerHTML = '<p style="padding:48px">กำลังโหลดข้อมูล…</p>';
    const boot = await bootstrapLive();
    if (boot === 'ok') {
      await handleTikTokRedirect();
      mountAppRouter();
    } else if (boot === 'no-account') {
      showOnboarding(); // สมัครแล้วแต่ยังไม่มีกิจการ → ตั้งค่ากิจการแรก
    } else {
      signOut();
      showLogin();
    }
  } else if (isConfigured) {
    showLogin();
  } else {
    // โหมด demo: ข้อมูลตัวอย่างอยู่ในเดือน เม.ย. 2025 → ตั้งช่วงเริ่มต้นให้ตรงชุดข้อมูล
    const f = initFilters({ from: '2025-04-01', to: '2025-04-30', compare: 'prev_month' });
    setView(demoView({ from: f.from, to: f.to }));
    mountAppRouter();
  }
}

void start();
